import { ProductForm } from "@/components/forms/product-form";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Product, productDataSchema, type ProductData } from "@/core/entities/product";
import { useAirlineIconUpload, useUpdateProduct } from "@/hooks";
import { useProductForm } from "@/hooks/forms/entities/use-product-form";
import { useCallback, useEffect, useState } from "react";

interface ProductEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  product: Product | null;
}

export function ProductEditDialog({
  open,
  onOpenChange,
  product,
}: Readonly<ProductEditDialogProps>) {
  const updateProductMutation = useUpdateProduct();
  const [showCloseConfirmation, setShowCloseConfirmation] = useState(false);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [pendingLogoSave, setPendingLogoSave] = useState(false);

  const {
    isLoading: isUploading,
    isComplete: isUploadComplete,
    uploadFile,
    url,
    error: uploadError,
  } = useAirlineIconUpload();

  const form = useProductForm({
    mode: "edit",
    product: product,
  });

  const onSubmit = async () => {
    console.log("onSubmit", imageFile);
    if (imageFile) {
      console.log("uploading image");
      try {
        setPendingLogoSave(true);
        uploadFile(imageFile);
      } catch (error) {
        console.error("Failed to upload image:", uploadError, error);
      }
    } else {
      console.log("no image file, submitting form");
      const formData = form.getValues();
      const data = productDataSchema.parse(formData);
      await mutateProduct(data);
      onOpenChange(false);
    }
  };

  const { mutateAsync: updateProduct } = updateProductMutation;
  const productId = product?.id;

  const mutateProduct = useCallback(
    async (data: ProductData) => {
      if (!productId) {
        console.error("No product ID available for update");
        return;
      }

      const submitData = { ...data };
      if (imageFile && !url) {
        return;
      }

      const productData: ProductData & { id: string } = {
        ...submitData,
        id: productId,
      };

      try {
        await updateProduct({
          id: productData.id,
          data: productData,
        });
      } catch (error) {
        console.error("Failed to update product:", error);
        throw error;
      }
    },
    [productId, imageFile, url, updateProduct],
  );

  // Save once per submit: the upload hook keeps `url` and `isComplete` set
  // after the first upload, so without this flag every later render re-saved.
  useEffect(() => {
    if (pendingLogoSave && url && isUploadComplete) {
      setPendingLogoSave(false);
      (async () => {
        const formData = form.getValues();
        const data = productDataSchema.parse({ ...formData, logo: url });
        await mutateProduct(data);
        onOpenChange(false);
        setImageFile(null);
        console.log("Image uploaded successfully");
      })();
    }
  }, [
    pendingLogoSave,
    isUploadComplete,
    url,
    form,
    onOpenChange,
    mutateProduct,
  ]);

  const handleCloseRequest = () => {
    setShowCloseConfirmation(true);
  };

  const handleConfirmClose = () => {
    form.reset();
    setShowCloseConfirmation(false);
    onOpenChange(false);
  };

  const handleCancelClose = () => {
    setShowCloseConfirmation(false);
  };

  return (
    <>
      <Sheet open={open} onOpenChange={handleCloseRequest}>
        <SheetContent
          side="right"
          className="w-full md:max-w-4xl  max-w-full overflow-y-auto p-4 sm:p-8 md:p-12"
        >
          <SheetHeader className="p-0">
            <SheetTitle>Edit Product</SheetTitle>
            <SheetDescription>
              Modify the product information and pricing configuration
            </SheetDescription>
          </SheetHeader>
          <ProductForm
            form={form}
            onSubmit={onSubmit}
            onImageChange={setImageFile}
            onCancel={() => onOpenChange(false)}
            submitText="Update Product"
            imageUrl={url ?? product?.logo ?? ""}
            isLoading={isUploading}
          />
        </SheetContent>
      </Sheet>

      <AlertDialog
        open={showCloseConfirmation}
        onOpenChange={setShowCloseConfirmation}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Close Product Edit</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to close? All unsaved changes will be lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancelClose}>
              Continue Editing
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmClose}>
              Close
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
